import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  Paper, 
  Box, 
  Grid, 
  CircularProgress,
  Button as MuiButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import { Home as HomeIcon, ArrowBack as ArrowBackIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import { 
  SemanticAnalysisRequest,
  useStartSemanticAnalysis,
  useGetSemanticAnalysisStatus,
  useGetSemanticAnalysisByWorkflow,
  useExportToModule4,
  useGetBlobFiles
} from '../services';
import type { SemanticAnalysisStatus, SemanticFramework } from '../services/semanticService';
import { useApp } from '../context/AppContext';
import { useDatabaseMode } from '../context/DatabaseModeContext';
import { useWorkflowContext } from '../context/WorkflowContext';
import WorkflowStepper from '../components/WorkflowStepper';
import { getWorkflowStatus } from '../utils/moduleStatus';
import { clientService } from '../services/api/clientService';
import SemanticFrameworkResults from '../components/SemanticFrameworkResults';
import ModuleActionCard from '../components/common/ModuleActionCard';

// Import our custom hooks
import {
  useWorkflowNavigation,
  useSemanticAnalysisData,
  useFileSelection,
  useUIState
} from '../components/module3/hooks';

// Import our UI components
import {
  FileSelectionForm,
  ExportResults,
  ExportResultsModal
} from '../components/module3';

const Module3SemanticAnalysis = () => {
  const { workflowData, updateWorkflowData, workflowId: contextWorkflowId, setWorkflowId } = useApp();
  const { isUsingDatabaseApproach, setIsUsingDatabaseApproach } = useDatabaseMode();
  const { triggerRefresh } = useWorkflowContext();
  const { clientId: urlClientId, workflowId: urlWorkflowId } = useParams();
  const navigate = useNavigate();
  
  // Use URL workflow ID if available, otherwise fall back to context
  const workflowId = urlWorkflowId || contextWorkflowId;
  
  const [clientId, setClientId] = useState('');
  const [forceReanalysis, setForceReanalysis] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<SemanticAnalysisStatus | null>(null);
  const [selectedFramework, setSelectedFramework] = useState<SemanticFramework | null>(() => {
    // Try to load framework from localStorage when component mounts
    if (workflowId) {
      const storedFramework = localStorage.getItem(`module3_${workflowId}_framework`);
      return storedFramework ? JSON.parse(storedFramework) : null;
    }
    return null;
  });
  const [isLoadingFramework, setIsLoadingFramework] = useState(false);
  
  // Database approach is now managed by context
  const [activeModuleId, setActiveModuleId] = useState<number | null>(() => {
    if (workflowId) {
      const storedId = localStorage.getItem(`module3_${workflowId}_module_id`);
      return storedId ? parseInt(storedId, 10) : null;
    }
    return null;
  });
  const [dbWorkflowId, setDbWorkflowId] = useState<number | null>(() => {
    const storedId = localStorage.getItem('db_workflow_id');
    return storedId ? parseInt(storedId, 10) : null;
  });
  
  const startSemanticAnalysisMutation = useStartSemanticAnalysis();
  const semanticAnalysisStatusQuery = useGetSemanticAnalysisByWorkflow(
    workflowId || '', 
    clientId || ''
  );
  
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const analysisStatusQuery = useGetSemanticAnalysisStatus(
    analysisId || '', 
    clientId || '', 
    workflowId || ''
  );
  
  const [exportConfig, setExportConfig] = useState<{
    config_id?: string;
    name?: string;
    description?: string;
  }>({});
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportResult, setExportResult] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [clientData, setClientData] = useState<any>(null);
  const [exportedConfig, setExportedConfig] = useState<any>(() => {
    // Check localStorage for a previously exported config for this workflow
    if (workflowId) {
      const storedConfig = localStorage.getItem(`module3_${workflowId}_exported_config`);
      return storedConfig ? JSON.parse(storedConfig) : null;
    }
    return null;
  });
  
  const exportToModule4Mutation = useExportToModule4();
  
  const [selectedTopicFrameworkFile, setSelectedTopicFrameworkFile] = useState<string>('');
  const [selectedRelevantKeywordsFile, setSelectedRelevantKeywordsFile] = useState<string>('');
  const [topicFrameworkFiles, setTopicFrameworkFiles] = useState<{label: string, value: string, path: string, isSample: boolean}[]>([]);
  const [relevantKeywordsFiles, setRelevantKeywordsFiles] = useState<{label: string, value: string, path: string, isSample: boolean}[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
  
  const { data: blobFiles, isLoading: isLoadingBlobFiles } = useGetBlobFiles(
    workflowId || '', 
    clientId || ''
  );
  
  const [usingMockData, setUsingMockData] = useState<boolean>(false);
  
  const [showConfigSection, setShowConfigSection] = useState(true);
  
  const [stepperCollapsed, setStepperCollapsed] = useState(() => {
    return localStorage.getItem('module3_stepper_collapsed') === 'true';
  });
  
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState<string | null>(null);

  // Sync URL workflow ID with App context
  useEffect(() => {
    if (urlWorkflowId && urlWorkflowId !== contextWorkflowId) {
      setWorkflowId(urlWorkflowId);
    }
  }, [urlWorkflowId, contextWorkflowId, setWorkflowId]);

  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (urlClientId && /^\d+$/.test(urlClientId)) {
        try {
          const client = await clientService.getClient(parseInt(urlClientId, 10));
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };

    fetchClientData();
  }, [urlClientId]);

  const handleStepperCollapseChange = (collapsed: boolean) => {
    setStepperCollapsed(collapsed);
    localStorage.setItem('module3_stepper_collapsed', collapsed.toString());
  };

  const handleDatabaseToggle = (newValue: boolean) => {
    setIsUsingDatabaseApproach(newValue);
  };
  
  const handleStartAnalysis = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientId.trim()) {
      setError('Please enter a client ID');
      return;
    }
    
    if (!selectedRelevantKeywordsFile) {
      setError('Please select a relevant keywords file');
      return;
    }
    
    if (!selectedTopicFrameworkFile) {
      setError('Please select a topic framework file');
      return;
    }
    
    setIsAnalyzing(true);
    setError(null);
    setAnalysisStatus(null);
    setSelectedFramework(null);
    // Clear stored framework when starting new analysis
    if (workflowId) {
      localStorage.removeItem(`module3_${workflowId}_framework`);
    }
    
    try {
      const request: SemanticAnalysisRequest = {
        workflow_id: workflowId || '',
        client_id: clientId,
        relevant_keywords_blob: selectedRelevantKeywordsFile,
        undecided_keywords_blob: selectedRelevantKeywordsFile.replace('relevant.csv', 'undecided.csv'),
        topic_framework_blob: selectedTopicFrameworkFile,
        suggested_categories: [],
        forceReanalysis: forceReanalysis,
        ...(customPrompt ? { custom_prompt: customPrompt } : {})
      };
      
      const response = await startSemanticAnalysisMutation.mutateAsync(request);
      console.log('Analysis started with response:', response);
      
      if (response.analysis_id) {
        setAnalysisId(response.analysis_id);
      }
      
      setAnalysisStatus(response);
      
      // Trigger refresh to update stepper status
      if (triggerRefresh) {
        triggerRefresh();
      }
    } catch (error) {
      console.error('Error starting semantic analysis:', error);
      setIsAnalyzing(false);
      setError(error instanceof Error ? error.message : 'Failed to start analysis');
    }
  };
  
  const handleCopyConfig = () => {
    if (exportedConfig) {
      navigator.clipboard.writeText(JSON.stringify(exportedConfig, null, 2))
        .then(() => {
          console.log('Configuration copied to clipboard');
        })
        .catch(err => {
          console.error('Failed to copy config:', err);
        });
    }
  };
  
  const handleNavigateToModule4 = () => {
    let path = '/module4';
    // Navigate to Module 4 configuration page with the current workflow ID
    if (clientId && workflowId) {
      path = `/module4/client/${clientId}/${workflowId}`;
    } else if (workflowId) {
      path = `/module4/${workflowId}`;
    } else if (clientId) {
      path = `/module4/client/${clientId}`;
    }
    navigate(path);
  };
  
  const handleExportToModule4 = async (configName: string, configDescription: string) => {
    if (!selectedFramework) return;
    
    setIsExporting(true);
    setExportError(null);
    setExportResult(false);
    
    try {
      // Debug logging
      console.log('Selected framework:', selectedFramework);
      console.log('Framework ID:', selectedFramework.framework_id);
      console.log('Workflow ID:', workflowId);
      console.log('Client ID:', clientId);
      
      // Use 'generated' as fallback if framework_id is missing
      const frameworkId = selectedFramework.framework_id || 'generated';
      
      const result = await exportToModule4Mutation.mutateAsync({
        workflow_id: workflowId || '',
        framework_id: frameworkId,
        client_id: clientId,
        config: {
          config_id: exportConfig.config_id,
          name: configName,
          description: configDescription
        }
      });
      
      setExportedConfig(result);
      setExportResult(true);
      
      // Persist the exported config in localStorage
      if (workflowId) {
        localStorage.setItem(`module3_${workflowId}_exported_config`, JSON.stringify(result));
      }
      
      // Store the most recent Module 4 module ID for this workflow if it's available in the result
      if (result && result.module_id) {
        const moduleId = result.module_id;
        localStorage.setItem(`workflow_${workflowId}_module4_id`, moduleId.toString());
        console.log(`Stored latest Module 4 ID (${moduleId}) for workflow ${workflowId}`);
        
        // Enable database-first approach since we saved configuration to database
        if (!isUsingDatabaseApproach) {
          setIsUsingDatabaseApproach(true);
        }
        
        // Set the active module ID
        setActiveModuleId(moduleId);
      }
    } catch (error) {
      console.error('Error exporting to Module 4:', error);
      setExportError(error instanceof Error ? error.message : 'Failed to export to Module 4');
    } finally {
      setIsExporting(false);
    }
  };
  
  const handleOpenExportModal = () => {
    setExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setExportModalOpen(false);
  };

  const handleExport = () => {
    if (selectedFramework && clientId && workflowId) {
      const configName = `Keyword Classification Framework - Workflow ${workflowId}`;
      const configDescription = "Semantic keyword classification framework for search intent analysis";
      handleExportToModule4(configName, configDescription);
    }
  };

  useWorkflowNavigation(workflowId, navigate, urlClientId, setClientId);
  
  useSemanticAnalysisData(
    semanticAnalysisStatusQuery,
    analysisStatusQuery,
    isAnalyzing,
    setIsAnalyzing,
    setAnalysisStatus,
    setAnalysisId,
    setSelectedFramework,
    selectedFramework,
    setError,
    workflowData,
    updateWorkflowData,
    workflowId,
    clientId,
    triggerRefresh
  );
  
  useFileSelection(
    blobFiles,
    workflowId,
    clientId,
    setIsLoadingFiles,
    setUsingMockData,
    setTopicFrameworkFiles,
    setRelevantKeywordsFiles,
    setSelectedTopicFrameworkFile,
    setSelectedRelevantKeywordsFile
  );
  
  useUIState(selectedFramework, setShowConfigSection);
  
  useEffect(() => {
    if (selectedTopicFrameworkFile && selectedRelevantKeywordsFile) {
      console.log('Selected files:', {
        topicFramework: selectedTopicFrameworkFile,
        relevantKeywords: selectedRelevantKeywordsFile
      });
      // Persist file selections to localStorage
      if (workflowId) {
        localStorage.setItem(`module3_${workflowId}_topic_framework_file`, selectedTopicFrameworkFile);
        localStorage.setItem(`module3_${workflowId}_relevant_keywords_file`, selectedRelevantKeywordsFile);
      }
    }
  }, [selectedTopicFrameworkFile, selectedRelevantKeywordsFile, workflowId]);
  
  if (!workflowId) {
    return null;
  }

    return (
    <Box className="container mx-auto py-6">
      <Grid container spacing={2} columnGap={!stepperCollapsed ? 4 : 0}>
        <Grid item xs="auto">
          <WorkflowStepper 
            activeStep={2} 
            workflowId={workflowId || ''} 
            clientId={urlClientId}
            moduleCompletionStatus={getWorkflowStatus(workflowId)}
            onCollapseChange={handleStepperCollapseChange}
          />
        </Grid>

        <Grid 
          item 
          xs
          sx={{
            pl: stepperCollapsed ? 1 : 3  // More left padding when stepper is open
          }}
        >
          {/* Breadcrumb navigation with back button */}
          {urlClientId && workflowId && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Breadcrumbs 
                aria-label="breadcrumb" 
                sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
              >
                <Link 
                  color="inherit" 
                  href="/" 
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Dashboard
                </Link>
                <Typography color="text.secondary">
                  {clientData?.name || `Client ${urlClientId}`} ({urlClientId} - {workflowId})
                </Typography>
                <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  Module 3: Semantic Analysis
                </Typography>
              </Breadcrumbs>
              
              <MuiButton
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/start-process')}
                sx={{ ml: 2 }}
              >
                Start Process
              </MuiButton>
            </Box>
          )}
          <Grid container spacing={3} sx={{ height: '100%' }}>
            {/* Input Form - Only shown if no results or explicitly toggled */}
            {showConfigSection && (
              <Grid item xs={12} md={stepperCollapsed ? 6 : 12} lg={analysisStatus ? 6 : 12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    Analysis Configuration
                  </Typography>
                  
                  {/* File Selection Form Component - Make it fill available space */}
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <FileSelectionForm 
                      clientId={clientId}
                      onClientIdChange={setClientId}
                      clientIdDisabled={!!urlClientId}
                      selectedTopicFrameworkFile={selectedTopicFrameworkFile}
                      onTopicFrameworkFileChange={setSelectedTopicFrameworkFile}
                      topicFrameworkFiles={topicFrameworkFiles}
                      selectedRelevantKeywordsFile={selectedRelevantKeywordsFile}
                      onRelevantKeywordsFileChange={setSelectedRelevantKeywordsFile}
                      relevantKeywordsFiles={relevantKeywordsFiles}
                      isLoadingFiles={isLoadingFiles}
                      usingMockData={usingMockData}
                      forceReanalysis={forceReanalysis}
                      onForceReanalysisChange={setForceReanalysis}
                      error={error}
                      isAnalyzing={isAnalyzing}
                      onSubmit={handleStartAnalysis}
                      customPrompt={customPrompt}
                      onCustomPromptChange={setCustomPrompt}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}
            
            {/* Results Display */}
            <Grid item xs={12} md={showConfigSection && stepperCollapsed ? 6 : 12} lg={showConfigSection ? 6 : 12} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Analysis Results
                  </Typography>
                  
                  {selectedFramework && (
                    <MuiButton 
                      size="small" 
                      variant="outlined"
                      onClick={() => setShowConfigSection(prev => !prev)}
                    >
                      {showConfigSection ? 'Hide Configuration' : 'Show Configuration'}
                    </MuiButton>
                  )}
                </Box>
                
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {isAnalyzing ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, flexGrow: 1 }}>
                      <CircularProgress size={48} sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Analyzing data...
                      </Typography>
                    </Box>
                  ) : (
                    <SemanticFrameworkResults 
                      framework={selectedFramework}
                      isLoading={isLoadingFramework}
                      workflowId={workflowId || ''}
                      clientId={clientId}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Module Action Card - Always visible */}
            <Grid item xs={12} sx={{ mb: 4 }}>
              <ModuleActionCard
                nextModuleNumber={4}
                isExporting={isExporting}
                exportSuccess={exportResult}
                onExport={handleExport}
                onViewExportResults={handleOpenExportModal}
                onGoToNextModule={handleNavigateToModule4}
                exportButtonText="Export to Module 4"
                nextModuleButtonText="Go to Module 4"
                disabled={!selectedFramework || isAnalyzing || isLoadingFramework}
              />
            </Grid>
          </Grid>       
        </Grid>
      </Grid>

      {/* Export Results Modal */}
      <ExportResultsModal
        open={exportModalOpen}
        onClose={handleCloseExportModal}
        exportedConfig={exportedConfig}
        exportError={exportError}
        exportResult={exportResult}
        onCopyConfig={handleCopyConfig}
      />
    </Box>
  );
};

export default Module3SemanticAnalysis;
